/**
 * Mobile bridge — exposes native Capacitor capabilities to the React app when
 * running inside the iOS / Android shells. Each helper falls back to the web
 * equivalent (file input, browser geolocation, etc.) when running in the
 * regular browser, so the SAME React codebase works in both environments.
 */
import { Capacitor } from "@capacitor/core";

export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform();

// ── Camera / Gallery ──
export async function takePhoto(opts = {}) {
  if (!isNative) {
    return new Promise((resolve) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      if (opts.source === "camera") input.setAttribute("capture", "environment");
      input.onchange = (e) => resolve(e.target.files?.[0] || null);
      input.click();
    });
  }
  const { Camera, CameraResultType, CameraSource } = await import("@capacitor/camera");
  const photo = await Camera.getPhoto({
    quality: opts.quality ?? 80,
    resultType: CameraResultType.Base64,
    source: opts.source === "camera" ? CameraSource.Camera : CameraSource.Photos,
    allowEditing: false,
  });
  if (!photo.base64String) return null;
  const blob = await fetch(`data:image/${photo.format};base64,${photo.base64String}`).then(r => r.blob());
  return new File([blob], `photo.${photo.format}`, { type: blob.type });
}

// ── Geolocation (DYD Run) ──
export async function getLocation() {
  if (!isNative) {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true });
    });
  }
  const { Geolocation } = await import("@capacitor/geolocation");
  return Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 });
}

export async function watchLocation(callback) {
  if (!isNative) {
    const id = navigator.geolocation.watchPosition(callback, console.warn, { enableHighAccuracy: true });
    return () => navigator.geolocation.clearWatch(id);
  }
  const { Geolocation } = await import("@capacitor/geolocation");
  const watchId = await Geolocation.watchPosition({ enableHighAccuracy: true }, (pos) => callback(pos));
  return () => Geolocation.clearWatch({ id: watchId });
}

// ── Share PDF / text ──
export async function shareContent(opts) {
  if (!isNative) {
    if (navigator.share) {
      try { return await navigator.share(opts); } catch {/* user cancelled */}
    }
    if (opts.url) window.open(opts.url, "_blank");
    return;
  }
  const { Share } = await import("@capacitor/share");
  await Share.share(opts);
}

// ── Push notifications ──
export async function registerPushNotifications(onToken) {
  if (!isNative) return null;
  const { PushNotifications } = await import("@capacitor/push-notifications");
  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== "granted") return null;
  await PushNotifications.register();
  await PushNotifications.addListener("registration", (t) => onToken(t.value));
  return PushNotifications;
}

// ── Secure storage (auth token) ──
export async function setSecure(key, value) {
  if (!isNative) { localStorage.setItem(key, value); return; }
  const { Preferences } = await import("@capacitor/preferences");
  await Preferences.set({ key, value });
}

export async function getSecure(key) {
  if (!isNative) return localStorage.getItem(key);
  const { Preferences } = await import("@capacitor/preferences");
  const r = await Preferences.get({ key });
  return r.value;
}

// ── External URL ──
export async function openBrowser(url) {
  if (!isNative) { window.open(url, "_blank"); return; }
  const { Browser } = await import("@capacitor/browser");
  await Browser.open({ url, presentationStyle: "popover" });
}

// ── Native startup config ──
export async function configureNativeStartup() {
  if (!isNative) return;
  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: "#0A0A0A" });
  } catch {/* */}
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    setTimeout(() => SplashScreen.hide(), 1500);
  } catch {/* */}
}
